import { useState } from "react"
import { Quote } from "lucide-react"
import { cn } from "@/lib/cn"
import { fmtRelativeTime } from "@/lib/format"
import { wordDiff, type DiffSegment } from "@/lib/word-diff"
import { Dialog, DialogBody, DialogFooter, DialogHeader } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import type { ChangeLogEntry, ChangeLogSource } from "@/types/change_log"

type Props = {
  entries: ChangeLogEntry[]
  // When true, renders compact cards (right-rail use); when false, renders
  // wider rows suitable for a bottom-of-page Card.
  compact?: boolean
}

const SOURCE_TONE: Record<ChangeLogSource, string> = {
  forge:  "bg-vip-gold/10 text-vip-gold border-vip-gold/30",
  manual: "bg-white/5 text-app-muted border-border",
  system: "bg-white/[0.03] text-app-subtle border-border",
  api:    "bg-sky-500/10 text-sky-300 border-sky-500/30",
}

// Reusable across every config surface (skills, agents, endpoints, …).
// Action-specific renderers live in a registry; unknown actions fall back
// to a generic JSON preview so a future action surfaces SOMETHING without
// a FE rebuild.
export function EditHistoryPane({ entries, compact = false }: Props) {
  if (entries.length === 0) {
    return (
      <div className="rounded-control border border-dashed border-border p-4 text-center text-xs text-app-muted">
        No edits yet — every save will show up here.
      </div>
    )
  }

  return (
    <div className={cn("space-y-3", compact ? "max-h-[60vh] overflow-y-auto pr-1" : null)}>
      {entries.map((e) => (
        <EntryRow key={e.id} entry={e} compact={compact} />
      ))}
    </div>
  )
}

function EntryRow({ entry, compact }: { entry: ChangeLogEntry; compact: boolean }) {
  const [diffOpen, setDiffOpen] = useState(false)
  // Short ~6-word headline. Priority order:
  //   1. Haiku's `subject` (gold standard — annotated post-write).
  //   2. The first ~6 words of the operator/Forge note. Better than the
  //      generic action label ("Edited prompt") because the note carries
  //      the *intent* of the change (e.g. "warmer tone" vs. "tighten
  //      cooldown copy"). Used while annotation is pending OR failed.
  //   3. The action-derived fallback ("Edited prompt", "Updated tools").
  const headline =
    entry.subject?.trim() ||
    truncateToWords(entry.note, 8) ||
    deriveSubject(entry)
  // Long-form intent goes in the modal — the operator/Forge note
  // verbatim, full-length, no truncation. Empty when the change was
  // recorded without a note (rare — generic field updates).
  const longIntent = entry.note?.trim() || ""
  const tone = SOURCE_TONE[entry.source as ChangeLogSource] ?? SOURCE_TONE.manual
  const userName = entry.user?.name ?? (entry.source === "system" ? "System" : entry.source === "api" ? "API" : "Unknown")

  return (
    <>
      <div className={cn(
        "rounded-control border border-border bg-app-bg",
        compact ? "p-3" : "p-4"
      )}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              <span className={cn("rounded-control border px-1.5 py-0.5 uppercase tracking-wide", tone)}>
                {entry.source}
              </span>
              <span className="text-app-muted">{prettyAction(entry.action)}</span>
              {entry.versionNumber != null && (
                <span className="text-app-subtle">
                  v{entry.versionNumber}{entry.versionState ? ` ${entry.versionState}` : ""}
                </span>
              )}
            </div>
            <div className="mt-1 text-sm font-medium text-app-text break-words">
              {headline}
            </div>
            <div className="mt-0.5 text-[11px] text-app-muted">
              {userName} · {fmtRelativeTime(entry.at)}
            </div>
          </div>
          {hasViewableDiff(entry) && (
            <button
              type="button"
              onClick={() => setDiffOpen(true)}
              className="shrink-0 rounded-control border border-border px-2 py-0.5 text-[11px] text-app-muted hover:bg-white/5 hover:text-app-text"
            >
              Show diff
            </button>
          )}
        </div>
      </div>

      <Dialog open={diffOpen} onClose={() => setDiffOpen(false)} size="xl">
        <DialogHeader title={headline} subtitle={`${prettyAction(entry.action)} · ${userName} · ${fmtRelativeTime(entry.at)}`} />
        <DialogBody className="max-h-[78vh] overflow-y-auto">
          {longIntent && (
            <div className="rounded-control border-l-2 border-vip-gold/60 bg-vip-gold/[0.06] px-4 py-3 text-sm leading-relaxed text-app-text whitespace-pre-wrap">
              <div className="mb-1 flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-vip-goldSoft">
                <Quote size={11} strokeWidth={2} />
                Intent
              </div>
              {longIntent}
            </div>
          )}
          <DiffRenderer entry={entry} />
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => setDiffOpen(false)}>Close</Button>
        </DialogFooter>
      </Dialog>
    </>
  )
}

function DiffRenderer({ entry }: { entry: ChangeLogEntry }) {
  switch (entry.action) {
    case "tool_keys_update":      return <ChipDiff diff={entry.diff} field="tool_keys" />
    case "set_collection":        return <ChipDiff diff={entry.diff} />
    case "prompt_section_update": return <PromptDiff diff={entry.diff} />
    case "field_update":          return <FieldUpdateDiff diff={entry.diff} />
    default:                      return <JsonDiff diff={entry.diff} />
  }
}

// Long-form prose fields render as a unified word-diff (same as Forge
// proposal cards). Short scalars stay in the compact red/green stack.
const PROSE_FIELDS = new Set(["prompt_md", "description", "notes", "name"])

function FieldUpdateDiff({ diff }: { diff: Record<string, unknown> }) {
  const entries = Object.entries(diff).filter(([, v]) =>
    v && typeof v === "object" && ("before" in (v as Record<string, unknown>) || "after" in (v as Record<string, unknown>))
  )
  if (entries.length === 0) return <JsonDiff diff={diff} />
  return (
    <div className="space-y-3 text-xs">
      {entries.map(([field, value]) => {
        const v = value as { before?: unknown; after?: unknown }
        const isProse = PROSE_FIELDS.has(field) && (typeof v.before === "string" || typeof v.after === "string")
        return (
          <div key={field}>
            <div className="mb-1 font-mono text-[10px] uppercase tracking-wide text-app-subtle">{field}</div>
            {isProse ? (
              <ProseDiff before={typeof v.before === "string" ? v.before : ""} after={typeof v.after === "string" ? v.after : ""} />
            ) : (
              <div className="grid gap-0.5">
                <div className="rounded bg-status-error/10 px-2 py-1 text-status-error">{formatScalar(v.before)}</div>
                <div className="rounded bg-status-success/10 px-2 py-1 text-status-success">{formatScalar(v.after)}</div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function PromptDiff({ diff }: { diff: Record<string, unknown> }) {
  const v = diff.prompt_md as { before?: string; after?: string } | undefined
  if (!v) return <JsonDiff diff={diff} />
  return <ProseDiff before={v.before ?? ""} after={v.after ?? ""} />
}

// Inline word-level prose diff — same rendering the Forge proposal cards
// use. Unchanged words stay muted; insertions are green, deletions are
// red+strikethrough, all in one continuous flow so multi-paragraph
// prompts stay readable.
function ProseDiff({ before, after }: { before: string; after: string }) {
  const segments: DiffSegment[] = wordDiff(before, after)
  const isCreation = before.trim().length === 0
  const stats = isCreation ? null : countChanges(segments)
  return (
    <div className="text-xs">
      <div className="mb-1 flex items-center gap-2 text-[10px] text-app-subtle">
        {isCreation
          ? <span className="text-vip-goldSoft">new content</span>
          : <span>edits</span>}
        {stats && stats.adds > 0 && <span className="text-status-success">+{stats.adds}</span>}
        {stats && stats.dels > 0 && <span className="text-status-error">−{stats.dels}</span>}
      </div>
      <div className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded border border-border bg-app-bg p-3 text-[12px] leading-relaxed text-app-text">
        {segments.length === 0 ? (
          <span className="text-app-muted">(no change)</span>
        ) : (
          segments.map((s, i) => <DiffChunk key={i} seg={s} />)
        )}
      </div>
    </div>
  )
}

function DiffChunk({ seg }: { seg: DiffSegment }) {
  if (seg.type === "equal") return <span className="text-app-muted">{seg.text}</span>
  if (seg.type === "add") {
    return <span className="rounded bg-status-success/15 text-status-success">{seg.text}</span>
  }
  return (
    <span className="rounded bg-status-error/15 text-status-error line-through decoration-status-error/60">
      {seg.text}
    </span>
  )
}

function countChanges(segs: DiffSegment[]): { adds: number; dels: number } {
  let adds = 0, dels = 0
  for (const s of segs) {
    if (s.type === "add")      adds += s.text.replace(/\s+/g, " ").trim().split(" ").filter(Boolean).length
    else if (s.type === "del") dels += s.text.replace(/\s+/g, " ").trim().split(" ").filter(Boolean).length
  }
  return { adds, dels }
}

function ChipDiff({ diff, field }: { diff: Record<string, unknown>; field?: string }) {
  type Pair = { before?: unknown; after?: unknown }
  // Find the {before, after} pair — explicit field name first, else the
  // first object value with before/after keys.
  let pair: Pair | undefined
  if (field && diff[field] && typeof diff[field] === "object") {
    pair = diff[field] as Pair
  }
  if (!pair) {
    const found = Object.values(diff).find((v): v is Pair => !!v && typeof v === "object" && ("before" in (v as Pair) || "after" in (v as Pair)))
    if (found) pair = found
  }
  if (!pair) return <JsonDiff diff={diff} />

  const before = Array.isArray(pair.before) ? (pair.before as unknown[]).map(String) : []
  const after  = Array.isArray(pair.after)  ? (pair.after  as unknown[]).map(String) : []
  const added   = after.filter((k) => !before.includes(k))
  const removed = before.filter((k) => !after.includes(k))

  return (
    <div className="flex flex-wrap gap-1 text-[11px]">
      {removed.map((k) => (
        <span key={`r-${k}`} className="rounded bg-status-error/10 px-1.5 py-0.5 text-status-error line-through">{k}</span>
      ))}
      {added.map((k) => (
        <span key={`a-${k}`} className="rounded bg-status-success/10 px-1.5 py-0.5 text-status-success">+{k}</span>
      ))}
      {removed.length === 0 && added.length === 0 && (
        <span className="text-app-subtle">No element changes.</span>
      )}
    </div>
  )
}

function JsonDiff({ diff }: { diff: Record<string, unknown> }) {
  return (
    <pre className="max-h-[55vh] overflow-auto whitespace-pre-wrap rounded bg-white/[0.02] p-3 text-[12px] leading-relaxed text-app-muted">
      {JSON.stringify(diff, null, 2)}
    </pre>
  )
}

function hasViewableDiff(entry: ChangeLogEntry): boolean {
  if (!entry.diff || typeof entry.diff !== "object") return false
  return Object.keys(entry.diff).length > 0
}

function deriveSubject(entry: ChangeLogEntry): string {
  // Used while annotation is "pending" / "failed" so the row is still
  // useful at a glance. Mirror the action vocabulary.
  switch (entry.action) {
    case "create":                 return "Created"
    case "destroy":                return "Deleted"
    case "version_create":         return "Created draft version"
    case "version_promote":        return "Promoted to live"
    case "version_archive":        return "Archived previous version"
    case "prompt_section_update":  return "Edited prompt"
    case "tool_keys_update":       return "Updated tools"
    case "tool_config_update":     return "Updated tool config"
    case "data_collection_update": return "Updated data collection"
    case "set_collection":         return "Updated collection"
    case "merge_into":             return "Merged into another row"
    case "state_change":           return formatStateChange(entry.diff) ?? "Changed state"
    case "reassign":               return "Reassigned"
    case "priority_change":        return "Changed priority"
    case "attachment_set":         return "Uploaded attachment"
    case "attachment_clear":       return "Cleared attachment"
    case "field_update": {
      const fields = Object.keys(entry.diff || {})
      return fields.length === 1 ? `Updated ${fields[0]}` : `Updated ${fields.length} fields`
    }
    default: return prettyAction(entry.action)
  }
}

function formatStateChange(diff: Record<string, unknown>): string | null {
  const v = diff.state as { before?: string; after?: string } | undefined
  if (!v?.before || !v?.after) return null
  return `${v.before} → ${v.after}`
}

function prettyAction(a: string): string {
  return a.replace(/_/g, " ")
}

// First N words of a free-text note — used as a row headline while
// Haiku's annotation is pending. Strips markdown bullets/headings the
// Forge note often opens with (e.g. "[Recommended] — restructure…")
// so the headline doesn't waste room on syntax.
function truncateToWords(s: string | null | undefined, n: number): string | null {
  if (!s) return null
  const cleaned = s
    .trim()
    .replace(/^\[[^\]]+\]\s*[—\-:]?\s*/, "")  // leading "[Recommended] — "
    .replace(/^[#>*\-]+\s*/, "")               // leading markdown chars
    .split(/\s+/)
    .filter(Boolean)
  if (cleaned.length === 0) return null
  const head = cleaned.slice(0, n).join(" ")
  return cleaned.length > n ? `${head}…` : head
}

function formatScalar(v: unknown): string {
  if (v === null || v === undefined) return "—"
  if (typeof v === "string") return v.length > 200 ? `${v.slice(0, 200)}…` : v
  return JSON.stringify(v)
}
